import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, DollarSign, Users, Activity, Shield, RefreshCw, BarChart3, Zap, Heart, School } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');

/**
 * LiveTractionCard — the investor-ready dashboard widget.
 * Shows real-time growth, engagement, safety impact, revenue, and a
 * campaign ROI table. Can render in two surfaces:
 *   • surface="admin"   → hits /api/admin/live-metrics + /api/admin/utm/roi
 *   • surface="vault"   → hits /api/vault/live-metrics + /api/admin/utm/roi
 *                         (utm/roi is admin-only; vault surface hides it if 403)
 */
const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + 'k';
  return String(n);
};
const money = (n, currency = '£') => (n === null || n === undefined) ? '—' : `${currency}${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const pct = (n) => (n === null || n === undefined) ? '—' : `${n}%`;

const Metric = ({ icon, label, value, sub, color = '#F5831F', tid }) => (
  <div className="bg-white rounded-lg border p-3" data-testid={tid} style={{ borderColor: '#E0E0E0' }}>
    <div className="flex items-center gap-2 mb-1">
      <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}1a`, color }}>{icon}</div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">{label}</div>
    </div>
    <div className="text-2xl font-black leading-tight" style={{ color: '#1A1209' }}>{value}</div>
    {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const LiveTractionCard = ({ surface = 'admin' }) => {
  const [metrics, setMetrics] = useState(null);
  const [roi, setRoi] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulseStatus, setPulseStatus] = useState(null);   // {sent, at}
  const [pulseSending, setPulseSending] = useState(false);

  const load = useCallback(async () => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    try {
      const base = surface === 'vault' ? '/api/vault/live-metrics' : '/api/admin/live-metrics';
      const mr = await fetch(`${API}${base}`, { headers: { 'Authorization': `Bearer ${t}` } });
      if (!mr.ok) throw new Error(`metrics ${mr.status}`);
      setMetrics(await mr.json());

      // ROI — admin-only. If 403, silently skip (vault surface).
      try {
        const rr = await fetch(`${API}/api/admin/utm/roi?days=90`, { headers: { 'Authorization': `Bearer ${t}` } });
        if (rr.ok) setRoi(await rr.json());
      } catch (_) { /* noop */ }
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [surface]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000); // live — refresh every 60s
    return () => clearInterval(iv);
  }, [load]);

  const sendPulse = async () => {
    if (!window.confirm("Send the Investor Pulse email to every Vault recipient now?\n\n(Uses live metrics. Per-week dedup applies unless already sent this week.)")) return;
    setPulseSending(true);
    setPulseStatus(null);
    try {
      const t = getToken();
      const r = await fetch(`${API}/api/admin/investor-pulse/send?force=true`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${t}` }
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setPulseStatus({ sent: d.emails_sent, at: new Date().toLocaleTimeString() });
    } catch (e) {
      setPulseStatus({ error: e.message });
    } finally {
      setPulseSending(false);
    }
  };

  if (loading && !metrics) {
    return <div className="bg-white rounded-lg border p-5 animate-pulse" data-testid="live-traction-loading" style={{ borderColor: '#E0E0E0', minHeight: '280px' }} />;
  }
  if (!metrics) {
    return <div className="bg-white rounded-lg border p-5 text-sm text-red-700" data-testid="live-traction-error" style={{ borderColor: '#E0E0E0' }}>{error || 'Unable to load metrics'}</div>;
  }

  const g = metrics.growth || {};
  const e = metrics.engagement || {};
  const s = metrics.safety || {};
  const r = metrics.revenue || {};
  const sc = metrics.schools || {};

  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid={`live-traction-${surface}`} style={{ borderColor: '#E0E0E0' }}>
      <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: '#F0F0F0', background: 'linear-gradient(135deg, rgba(245,131,31,0.08), rgba(46,170,92,0.05))' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F5831F', color: 'white' }}>
            <Zap size={18} />
          </div>
          <div>
            <h3 className="text-base font-black" style={{ color: '#1A1209' }}>Live Traction</h3>
            <p className="text-[11px] text-gray-500">
              Real-time · as of {new Date(metrics.as_of).toLocaleTimeString()}
              {surface === 'vault' && <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[9px] font-bold uppercase">Investor view</span>}
            </p>
          </div>
        </div>
        <button onClick={load} className="text-gray-400 hover:text-gray-700 p-1.5 rounded" title="Refresh" data-testid="live-traction-refresh"><RefreshCw size={14} /></button>
      </div>

      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <Metric icon={<Users size={12} />} label="Total Users" value={fmt(g.total_users)}
                sub={g.mom_growth_pct != null ? `${g.mom_growth_pct > 0 ? '+' : ''}${g.mom_growth_pct}% MoM` : `${fmt(g.new_30d)} last 30d`}
                color="#F5831F" tid="metric-users" />
        <Metric icon={<Activity size={12} />} label="DAU / MAU" value={`${fmt(e.dau)} / ${fmt(e.mau)}`}
                sub={`${e.stickiness_pct}% stickiness`}
                color="#2EAA5C" tid="metric-engagement" />
        <Metric icon={<DollarSign size={12} />} label="MRR" value={money(r.mrr)}
                sub={`ARR ${money(r.arr)} · ${r.active_subscriptions} active`}
                color="#7B4FD8" tid="metric-revenue" />
        <Metric icon={<Heart size={12} />} label="Crisis Handovers" value={fmt(s.crisis_handovers_total)}
                sub={`${s.crisis_handovers_7d} in last 7d`}
                color="#DC2626" tid="metric-safety" />
        <Metric icon={<TrendingUp size={12} />} label="New (24h)" value={fmt(g.new_24h)}
                sub={`${fmt(g.new_7d)} last 7d`}
                color="#F5831F" tid="metric-new" />
        <Metric icon={<BarChart3 size={12} />} label="30d Retention" value={pct(e.retention_30d_pct)}
                sub={`${fmt(e.messages_7d)} msgs/7d`}
                color="#2EAA5C" tid="metric-retention" />
        <Metric icon={<School size={12} />} label="Schools" value={fmt(sc.total)}
                sub={`${fmt(sc.active)} active / pilot`}
                color="#7B4FD8" tid="metric-schools" />
        <Metric icon={<Shield size={12} />} label="Waitlist" value={fmt(metrics.waitlist?.total)}
                sub={`${fmt(metrics.waitlist?.last_30d)} last 30d · confirmed sign-ups only — ${fmt(metrics.waitlist?.marketing_leads_total)} marketing leads tracked separately in the campaign pipeline`}
                color="#1A1209" tid="metric-waitlist" />
      </div>

      {roi && roi.campaigns && roi.campaigns.length > 0 && (
        <div className="border-t" style={{ borderColor: '#F0F0F0' }}>
          <div className="px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 size={14} style={{ color: '#F5831F' }} />
              <h4 className="text-sm font-bold" style={{ color: '#1A1209' }}>Campaign ROI (90 days)</h4>
            </div>
            <div className="flex gap-4 text-[11px] text-gray-500">
              <span><b style={{ color: '#1A1209' }}>{roi.totals?.signups || 0}</b> signups</span>
              <span><b style={{ color: '#1A1209' }}>{roi.totals?.paying || 0}</b> paying</span>
              <span><b style={{ color: '#2EAA5C' }}>{money(roi.totals?.revenue)}</b> revenue</span>
              {roi.totals?.roi_pct != null && (
                <span>ROI <b style={{ color: roi.totals.roi_pct >= 0 ? '#2EAA5C' : '#DC2626' }}>{roi.totals.roi_pct}%</b></span>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="campaign-roi-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b" style={{ borderColor: '#F0F0F0' }}>
                  <th className="text-left px-5 py-2 font-semibold">Campaign</th>
                  <th className="text-right px-3 py-2 font-semibold">Signups</th>
                  <th className="text-right px-3 py-2 font-semibold">Paying</th>
                  <th className="text-right px-3 py-2 font-semibold">Revenue</th>
                  <th className="text-right px-3 py-2 font-semibold">Spend</th>
                  <th className="text-right px-5 py-2 font-semibold">ROI</th>
                </tr>
              </thead>
              <tbody>
                {roi.campaigns.slice(0, 8).map((c, i) => (
                  <tr key={c.campaign} className="border-b" style={{ borderColor: '#F5F5F5' }} data-testid={`roi-row-${i}`}>
                    <td className="px-5 py-2.5 font-semibold" style={{ color: '#1A1209' }}>{c.campaign}</td>
                    <td className="text-right px-3 py-2.5 tabular-nums">{c.signups}</td>
                    <td className="text-right px-3 py-2.5 tabular-nums">{c.paying}</td>
                    <td className="text-right px-3 py-2.5 tabular-nums" style={{ color: '#2EAA5C' }}>{money(c.revenue)}</td>
                    <td className="text-right px-3 py-2.5 tabular-nums text-gray-500">{c.spend != null ? money(c.spend) : '—'}</td>
                    <td className="text-right px-5 py-2.5 tabular-nums font-semibold" style={{ color: c.roi_pct == null ? '#6B6B6B' : (c.roi_pct >= 0 ? '#2EAA5C' : '#DC2626') }}>
                      {c.roi_pct != null ? `${c.roi_pct}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-2.5 border-t text-[11px] text-gray-500 flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: '#F0F0F0' }}>
            <span>Spend figures come from <code>POST /api/admin/utm/spend</code>. Add a row per campaign to unlock ROI columns.</span>
            {surface === 'admin' && (
              <div className="flex items-center gap-2">
                <a href={`${API}/api/admin/investor-pulse/preview`} target="_blank" rel="noopener noreferrer"
                   className="text-[11px] font-semibold text-gray-600 hover:text-gray-900 underline"
                   data-testid="investor-pulse-preview">Preview Investor Pulse</a>
                <button onClick={sendPulse} disabled={pulseSending}
                        className="text-[11px] font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                        style={{ backgroundColor: '#F5831F', color: 'white' }}
                        data-testid="investor-pulse-send">
                  {pulseSending ? 'Sending…' : 'Send Pulse Now'}
                </button>
                {pulseStatus && !pulseStatus.error && <span className="text-green-700 font-semibold">Sent {pulseStatus.sent} · {pulseStatus.at}</span>}
                {pulseStatus?.error && <span className="text-red-700 font-semibold">Error: {pulseStatus.error}</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTractionCard;
